import cx from "classnames";
import _ from "lodash";
import { Play } from "lucide-react";
import React from "react";
import { BoolCell } from "../components/grid/cells/BoolCell";
import { CheckmarkCell } from "../components/grid/cells/CheckmarkCell";
import { DueDateCell } from "../components/grid/cells/DueDateCell";
import {
  ASSIGNEE,
  CASE_COLLECTION_DATE,
  CASE_DATE_OF_SERVICE,
  CASE_LAB,
  CASE_LAB_ORDER_ID,
  CASE_LAB_PATIENT_ID,
  CASE_PATIENT_DOB,
  CASE_PATIENT_NAME,
  CASE_PHYSICIAN_NAME,
  CASE_PHYSICIAN_NPI,
  CURRENT_STATUS,
  CURRENT_STEP,
  CURRENT_SUBSTATUS,
  INSURANCE_MEMBER_ID,
  INSURANCE_NAME,
  PAYOR_PATHS,
  PHYSICIAN_PRACTICE,
  PUBLIC_ID,
} from "../components/grid/columns";
import { DateFilter, EnumFilter } from "../components/inputs";
import { formatDate, objectToOptions } from "../util/formatters";
import { trackEvent } from "../util/track";
import { PayorSelector } from "../components/inputs/PayorSelector";
import { TooltipCell } from "../components/grid/cells/TooltipCell";
import { AdministrateEnumFilter } from "../system_config/components/AdministrateEnumFilter";
import { api, redirectTo } from "../util/api";
import { Button } from "reactstrap";

const handleProcess = (caseId) => {
  trackEvent("initial_process", caseId, {
    step: "initial_process",
  });
};

const createPreprocessingCategoriesOptions = (categories) => {
  return categories.map((category) => ({ label: category.name, value: category.id }));
};

const handlePreprocess = async (caseId) => {
  // assigns the user who pushed the button
  const _req = await api.post(`/cases/${caseId}/preprocess_to_current_user`);

  await trackEvent("assigned_for_preprocessing", caseId, {
    step: "assigned_for_preprocessing",
  });

  redirectTo(`/operations/cases/${caseId}/verified_fields`);
};

const Links = ({ row }) => {
  const id = row.original.public_id;
  const requestId = _.get(row, "original.pa_request.requestId");
  const disabled = row.original.closed;

  return (
    <>
      {row.original?.cached_workflow_status === "preprocessing" ? (
        <>
          <Button color="warning" outline size="sm" onClick={() => handlePreprocess(id)} className="me-2">
            <span className="btn__text">Preprocess</span>
          </Button>
        </>
      ) : (
        <>
          <a onClick={() => handleProcess(id)} href={`/workflow/${id}`} className={cx("btn btn-sm btn-outline-primary me-2", { disabled })}>
            <Play className="btn__icon" />
            <span className="btn__text">Process</span>
          </a>
        </>
      )}

      <a className="btn btn-sm btn-outline-secondary" href={`/operations/cases/${requestId}`}>
        Details
      </a>
    </>
  );
};

export const PROCESS = {
  Header: "Actions",
  id: "process",
  disableFilters: true,
  disableSortBy: true,
  Cell: Links,
  className: "action-cell",
  minWidth: 220,
  maxWidth: 250,
};

const CLOSED = {
  Header: "Closed",
  id: "closed",
  accessor: (r) => _.get(r, "closed", false),
  minWidth: 85,
  Cell: CheckmarkCell,
  disableFilters: true,
};

const CURRENT_WORKFLOW = {
  Header: "Current Workflow",
  accessor: "workflow_state",
  minWidth: 180,
  Filter: (props) => <EnumFilter {...props} options={objectToOptions(window.available_workflows)} />,
};

export const TASK_DUE = {
  Header: "Task Due",
  id: "due_date",
  accessor: "due_date",
  minWidth: 125,
  Filter: (props) => <DateFilter {...props} />,
  Cell: DueDateCell,
};

// Memoize required to ensure that PayorSelector can maintain state
const VERIFIED_PAYOR = _.memoize((initialPayors) => ({
  Header: "Verified Payor",
  id: "payor",
  accessor: "pa_request.verifiedPayorName",
  minWidth: 140,
  Filter: ({ column: { filterValue, setFilter }, ...rest }) => {
    return (
      <PayorSelector
        isClearable
        isSearchable
        value={filterValue}
        onChange={setFilter}
        initialOptions={initialPayors}
        menuPortalTarget={document.body}
        placeholder="Show All"
        {...rest}
      />
    );
  },
  Cell: TooltipCell,
}));

const LAST_UPDATED = {
  Header: "Last Updated",
  id: "last_action_taken_at",
  accessor: (r) => formatDate(_.get(r, "last_action_taken_at")),
  minWidth: 130,
  Filter: (props) => <DateFilter {...props} />,
};

export const CREATED_ON = {
  Header: "Created On",
  id: "created_at",
  accessor: (r) => formatDate(_.get(r, "created_at")),
  minWidth: 130,
  Filter: (props) => <DateFilter {...props} />,
};

export const PREPROCESSING_CATEGORIES = (preprocessingCategories) => {
  const rawDefaultOptions = window.available_preprocessing_categories.filter((option) => preprocessingCategories.includes(option.id));
  const defaultOptions = createPreprocessingCategoriesOptions(rawDefaultOptions);

  return {
    Header: "Preprocessing Categories",
    id: "preprocessing_categories",
    accessor: (r) => {
      const categories = r.preprocessing_categories;

      return categories.map((category) => category.name).join(" ");
    },
    minWidth: 200,
    Filter: (props) => (
      <AdministrateEnumFilter
        resourceUrl="/admin/preprocessing_categories.json"
        isMulti
        valueKey="id"
        labelKey="name"
        {...props}
        initialOptions={defaultOptions}
      />
    ),
  };
};

export const REQUIRES_US_ASSIGNEE = {
  Header: "Offshore Restrictions",
  id: "requires_us_assignee",
  accessor: "requires_us_assignee",
  Cell: BoolCell,
  Filter: (props) => (
    <EnumFilter
      {...props}
      options={[
        { value: true, label: "Yes" },
        { value: false, label: "No" },
      ]}
    />
  ),
};

const ColumnWrapper = ({ shouldExec = false, props, Element }) => {
  return shouldExec ? Element(props) : Element;
};

const COLUMN_MAP = {
  public_id: {
    id: "public_id",
    Element: ColumnWrapper({ Element: PUBLIC_ID }),
  },
  current_workflow: {
    id: "workflow_state",
    Element: ColumnWrapper({ Element: CURRENT_WORKFLOW }),
  },
  current_step: {
    id: "cached_workflow_step",
    Element: ColumnWrapper({ Element: CURRENT_STEP }),
  },
  status: {
    id: "status",
    Element: ColumnWrapper({ Element: CURRENT_STATUS }),
  },
  substatus: {
    id: "substatus",
    Element: ColumnWrapper({ Element: CURRENT_SUBSTATUS }),
  },
  payor_paths: {
    id: "payor_paths",
    Element: ColumnWrapper({ Element: PAYOR_PATHS }),
  },
  assignee: {
    id: "assignee",
    Element: (props) => ColumnWrapper({ shouldExec: true, props: props, Element: ASSIGNEE }),
    preloadOptions: true,
    preloadType: "assignee",
  },
  patient_name: {
    id: "patient_name",
    Element: ColumnWrapper({ Element: CASE_PATIENT_NAME(false, true) }),
  },
  patient_dob: {
    id: "pa_request.patientInfo.dob",
    Element: ColumnWrapper({ Element: CASE_PATIENT_DOB }),
  },
  lab: {
    id: "lab",
    Element: (props) => ColumnWrapper({ shouldExec: true, props: props, Element: CASE_LAB }),
    preloadOptions: true,
    preloadType: "lab",
  },
  verified_payor: {
    id: "verified_payor",
    Element: (props) => ColumnWrapper({ shouldExec: true, props: props, Element: VERIFIED_PAYOR }),
    preloadOptions: true,
    preloadType: "payor",
  },
  created_on: {
    id: "created_at",
    Element: ColumnWrapper({ Element: CREATED_ON }),
  },
  preprocessing_categories: {
    id: "preprocessing_categories",
    Element: (props) => ColumnWrapper({ shouldExec: true, props: props, Element: PREPROCESSING_CATEGORIES }),
    preloadOptions: true,
    preloadType: "preprocessing_categories",
  },
};

export const OPERATIONS_COLUMNS = (enabledColumns, prefilledColumns) => (filterOptions) => {
  const { assignedUsersWithActiveCases, initialPayorOptions, initialLabOptions } = filterOptions;

  if (enabledColumns && enabledColumns?.length > 0) {
    enabledColumns.map((column) => {
      const targetColumn = COLUMN_MAP[column.column_name];

      if (targetColumn?.preloadOptions) {
        switch (targetColumn?.preloadType) {
          case "lab":
            targetColumn.props = initialLabOptions;
            break;
          case "payor":
            targetColumn.props = initialPayorOptions;
            break;
          case "assignee":
            targetColumn.props = assignedUsersWithActiveCases;
            break;
          case "payor_paths":
            targetColumn.props = prefilledColumns.find((column) => column.id === "payor_paths")?.value || "";
            break;
          case "preprocessing_categories":
            targetColumn.props = prefilledColumns.find((column) => column.id === "preprocessing_categories")?.value || "";
            break;
          default:
            break;
        }
      }

      column.targetColumn = targetColumn;
    });

    const parsedColumns = enabledColumns.map((column) => {
      return column.targetColumn?.preloadOptions ? column.targetColumn.Element(column.targetColumn.props) : column.targetColumn.Element;
    });

    parsedColumns.unshift(PROCESS);

    return parsedColumns;
  }

  return [
    PROCESS,
    PUBLIC_ID,
    CURRENT_WORKFLOW,
    CURRENT_STEP,
    CURRENT_STATUS,
    CURRENT_SUBSTATUS,
    PAYOR_PATHS,
    ASSIGNEE(assignedUsersWithActiveCases),
    CASE_PATIENT_NAME(false, true),
    CASE_PATIENT_DOB,
    PHYSICIAN_PRACTICE,
    CASE_PHYSICIAN_NPI,
    CASE_PHYSICIAN_NAME,
    CASE_LAB_PATIENT_ID,
    REQUIRES_US_ASSIGNEE,
    CASE_LAB_ORDER_ID,
    CASE_LAB(initialLabOptions),
    CASE_COLLECTION_DATE,
    CASE_DATE_OF_SERVICE,
    TASK_DUE,
    VERIFIED_PAYOR(initialPayorOptions),
    INSURANCE_NAME,
    INSURANCE_MEMBER_ID,
    LAST_UPDATED,
    CREATED_ON,
  ];
};
